import React, { FC, useContext, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { DeviceLinking } from 'client/blocks/DeviceLinking/DeviceLinking';
import { GrammarPage } from 'client/blocks/grammar/GrammarPage/GrammarPage';
import Settings from 'client/blocks/Settings/Settings';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { useSettingsPageMenuItems } from 'client/hooks/useSettingsPageMenuItems';
import { isInDevelopment } from 'client/model/draft';
import { AliceSkill, SmartHome } from 'client/model/skill';
import { UserAgreement } from 'client/model/userAgreement';
import { getSettingsWithUserAgreements, isSmartHomeSkill } from 'client/utils';

interface Props {
    skill: AliceSkill | SmartHome;
    isDraft: boolean;
    userAgreements: UserAgreement[];
    userAgreementsDraft: UserAgreement[];
    disabled: boolean;
}

export const AliceSkillsAppSettingsPage: FC<Props> = ({
    skill,
    isDraft,
    userAgreements,
    userAgreementsDraft,
    disabled,
}) => {
    const [hasChanges, setHasChanges] = useState(false);

    const { display } = useSettingsPageMenuItems(skill, isDraft);
    const currentAliceAppContext = useContext(CurrentAliceAppContext);

    const settings = getSettingsWithUserAgreements(skill, isDraft, isDraft ? userAgreementsDraft : userAgreements);

    return (
        <div>
            <article>
                <Routes>
                    <Route
                        path="main"
                        element={
                            isDraft ? (
                                <Settings
                                    aliceApp={skill}
                                    isDraft
                                    registerChanges={setHasChanges}
                                    refreshAliceApp={currentAliceAppContext?.refreshAliceApp}
                                    fetchedUserAgreementsDraft={userAgreementsDraft}
                                    hasChanges={hasChanges}
                                    settings={settings}
                                    disabled={disabled}
                                />
                            ) : (
                                <Settings
                                    aliceApp={skill}
                                    isDraft={false}
                                    registerChanges={setHasChanges}
                                    hasChanges={hasChanges}
                                    settings={settings}
                                    disabled={disabled}
                                />
                            )
                        }
                    />
                    {display.devices && isSmartHomeSkill(skill) && (
                        <Route
                            path="devices"
                            element={
                                <DeviceLinking
                                    isDraft={isDraft}
                                    isEditable={isDraft && isInDevelopment(skill) && !disabled}
                                    aliceApp={skill}
                                />
                            }
                        />
                    )}
                    {display.intents && (
                        <Route path="intents" element={<GrammarPage aliceApp={skill} disabled={disabled} />} />
                    )}
                    <Route path="userAgreements" element={<Navigate to="main#user-agreements" replace />} />
                    <Route path="*" element={<Navigate to="../main" replace />} />
                </Routes>
            </article>
        </div>
    );
};
